import type { Auth, User } from "firebase/auth";
import { type FormEvent, Fragment, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { KEY_COLUMNS } from "./columns";
import { useTitle } from "./hooks";
import { endProblem, hubsText, KEY_STATUS_TEXT, labelProblem, lastDay, revokeText } from "./keys";
import { type Saved, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { type Failure, failureOf, Problem, Status } from "./UserPages";
import { Skeleton } from "./Skeleton";

type KeyList = { keys: api.ApiKey[]; hubs: api.Hub[] };

const day = (iso: string | null) => (iso ? dayText(iso.slice(0, 10), true) : "—");
const today = () => new Date().toISOString().slice(0, 10);

/** Design 11.4, 19.2, WF-14: the API keys that read the tanks. Only an administrator sees this page (rule U-13). */
export function KeyList({ org, auth, user, canView }: { org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<KeyList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [making, setMaking] = useState(false);
  const [made, setMade] = useState<(api.ApiKey & { key: string }) | null>(null);
  const [revoking, setRevoking] = useState<api.ApiKey | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [sort, setSort] = useSort("sort", KEY_COLUMNS);
  const questionRef = useRef<HTMLParagraphElement>(null);
  useTitle(`API keys · ${org.name}`);
  useEffect(() => {
    if (revoking) questionRef.current?.focus();
  }, [revoking]);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.apiKeys)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="keys-hidden">
        <h1 tabIndex={-1}>API keys</h1>
        <p>Only an administrator of the whole organization sees the API keys.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  const revoke = async (key: api.ApiKey) => {
    setSaving(true);
    setSaved(null);
    try {
      await api.revokeKey(await user.getIdToken(), key.id);
      setRevoking(null);
      setSaved({ ok: true, text: `Revoked "${key.label}". Calls with this key now fail.` });
      setAttempt((n) => n + 1);
      requestAnimationFrame(() => document.querySelector('[data-testid="key-status-line"]')?.scrollIntoView({ block: "nearest" }));
    } catch (e) {
      setSaved(saveFailure(e, "The change"));
    } finally {
      setSaving(false);
    }
  };
  const cancelRevoke = (key: api.ApiKey) => {
    setRevoking(null);
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`[data-testid="key-revoke"][data-key-id="${key.id}"]`)?.focus());
  };
  const revokedCount = list ? list.keys.filter((key) => key.status === "revoked").length : 0;
  const visible = list ? list.keys.filter((key) => showRevoked || key.status !== "revoked") : [];
  return (
    <main className="keys-page wide" data-testid="keys-page">
      <h1 tabIndex={-1}>API keys</h1>
      <p className="sub">A key lets another system read the container levels of this organization. It cannot change data.</p>
      {made && <NewKey made={made} onDone={() => setMade(null)} />}
      {list && !making && !made && (
        <div className="tabs">
          <button type="button" className="primary" data-testid="key-make" onClick={() => setMaking(true)}>
            Make a key
          </button>
        </div>
      )}
      {list && making && (
        <MakeForm
          list={list}
          auth={auth}
          user={user}
          onCancel={() => setMaking(false)}
          onMade={(key) => {
            setMaking(false);
            setMade(key);
            setAttempt((n) => n + 1);
          }}
        />
      )}
      <Status saved={saved} auth={auth} testId="key-status-line" />
      {found === undefined ? (
        <Skeleton shape="table" columns={4} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : !found.keys.length ? (
        <p data-testid="keys-empty">No API keys yet.</p>
      ) : (
        <>
          <SortSelect id="keys-select" columns={KEY_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          {revokedCount > 0 && (
            <label className="check" data-testid="keys-show-revoked">
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} /> Show revoked ({revokedCount})
            </label>
          )}
          {!visible.length ? (
            <p data-testid="keys-none-active">
              No active keys. Make a key, or select Show revoked ({revokedCount}).
            </p>
          ) : (
          <div className="table-scroll" tabIndex={0} role="region" aria-label="API keys">
            <table className="list" data-testid="key-table" data-cols="keys-select">
              <thead>
                <tr>
                  {KEY_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortRows(visible, KEY_COLUMNS, sort).map((key) => (
                  <Fragment key={key.id}>
                  <tr className={key.status === "active" ? undefined : "muted"} data-testid="key-row" data-label={key.label} data-status={key.status}>
                    <th scope="row">{key.label}</th>
                    <td data-label="Key ID">
                      <code>{key.kid}</code>
                    </td>
                    <td data-label="Hubs">{hubsText(key.hub_ids, found.hubs)}</td>
                    <td data-label="Status">{KEY_STATUS_TEXT[key.status]}</td>
                    <td data-label="Made">{day(key.created_at)}</td>
                    <td data-label="Last used">{key.last_used_at ? day(key.last_used_at) : "Never"}</td>
                    <td data-label="Ends">{key.expires_at ? day(lastDay(key.expires_at)) : "No end date"}</td>
                    <td>
                      {key.status === "active" && (
                        <button type="button" className="danger" data-testid="key-revoke" data-key-id={key.id} aria-expanded={revoking?.id === key.id} onClick={() => setRevoking(key)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                  {revoking?.id === key.id && (
                    <tr className="detail-row">
                      <td colSpan={KEY_COLUMNS.length + 1}>
                        <form
                          onSubmit={(event: FormEvent) => {
                            event.preventDefault();
                            void revoke(key);
                          }}
                          data-testid="key-revoke-form"
                          aria-labelledby="key-revoke-title"
                        >
                          <p id="key-revoke-title" ref={questionRef} tabIndex={-1}>
                            {revokeText(key.label)}
                          </p>
                          <div className="actions">
                            <button type="submit" className="danger" data-testid="key-revoke-confirm" disabled={saving}>
                              Revoke key
                            </button>
                            <button type="button" onClick={() => cancelRevoke(key)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </main>
  );
}

function NewKey({ made, onDone }: { made: api.ApiKey & { key: string }; onDone: () => void }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <section className="new-key" data-testid="key-made" aria-labelledby="key-made-title">
      <h2 id="key-made-title">Copy the key "{made.label}" now</h2>
      <p role="alert">This is the only time the full key shows. Keep it in a safe place. If you lose it, revoke it and make a new key.</p>
      <code className="secret" data-testid="key-secret">
        {made.key}
      </code>
      <div className="actions">
        <button
          type="button"
          className="primary"
          data-testid="key-copy"
          onClick={() =>
            navigator.clipboard
              .writeText(made.key)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          Copy the key
        </button>
        <button type="button" data-testid="key-done" onClick={onDone}>
          I have the key
        </button>
      </div>
      <p role="status">{copied === true ? "Copied." : copied === false ? "The copy did not work. Select the key and copy it." : ""}</p>
    </section>
  );
}

function MakeForm({
  list,
  auth,
  user,
  onCancel,
  onMade,
}: {
  list: KeyList;
  auth: Auth;
  user: User;
  onCancel: () => void;
  onMade: (key: api.ApiKey & { key: string }) => void;
}) {
  const [label, setLabel] = useState("");
  const [hubIds, setHubIds] = useState<string[]>([]);
  const [ends, setEnds] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const taken = list.keys.filter((key) => key.status === "active").map((key) => key.label);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = labelProblem(label, taken) ?? endProblem(ends, today());
    if (problem) return setSaved({ ok: false, text: problem });
    setSaving(true);
    setSaved(null);
    try {
      const entry = { label: label.trim().replace(/\s+/g, " "), hub_ids: hubIds, expires_on: ends || null };
      onMade(await api.createKey(await user.getIdToken(), entry));
    } catch (e) {
      setSaved(saveFailure(e, "The key"));
    } finally {
      setSaving(false);
    }
  };
  const toggle = (id: string) => setHubIds((before) => (before.includes(id) ? before.filter((hub) => hub !== id) : [...before, id]));
  return (
    <form onSubmit={submit} noValidate data-testid="key-form" aria-labelledby="key-form-title">
      <h2 id="key-form-title">Make an API key</h2>
      <label htmlFor="key-label">Name</label>
      <input id="key-label" data-testid="key-label" value={label} maxLength={80} autoComplete="off" onChange={(e) => setLabel(e.target.value)} />
      <p className="sub">Access: read the container levels. A key cannot change data.</p>
      {list.hubs.length > 0 && (
        <fieldset>
          <legend>Hubs</legend>
          <p className="sub">Select no hub to give the key all hubs.</p>
          {list.hubs.map((hub) => (
            <label key={hub.id} className="check">
              <input type="checkbox" checked={hubIds.includes(hub.id)} onChange={() => toggle(hub.id)} /> {hub.name}
            </label>
          ))}
        </fieldset>
      )}
      <label htmlFor="key-ends">Last day (optional)</label>
      <input id="key-ends" data-testid="key-ends" type="date" min={today()} value={ends} onChange={(e) => setEnds(e.target.value)} />
      <div className="actions">
        <button type="submit" className="primary" data-testid="key-create" disabled={saving}>
          Make the key
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="key-form-status" />
    </form>
  );
}
